/**
 * How a learner came to be enrolled, from the enrollment record the server
 * writes: bought (`paymentStatus: 'paid'`, `amount`), added by the
 * instructor (`source: 'instructor'` / `grantedBy`, paymentStatus 'comped'),
 * a free course, or no longer entitled (refunded, disputed, revoked). Same
 * entitlement rule as the server's isEnrollmentEntitled.
 */
export type EnrollmentKind = 'paid' | 'free' | 'added' | 'refunded' | 'disputed' | 'revoked';

export interface EnrollmentLike {
  paymentStatus?: unknown;
  source?: unknown;
  grantedBy?: unknown;
  accessRevokedBy?: unknown;
  status?: unknown;
  amount?: unknown;
  amountPaid?: unknown;
}

export function enrollmentKind(e: EnrollmentLike): EnrollmentKind {
  const payment = String(e.paymentStatus ?? '').toLowerCase();
  // A refund also removes access (accessRevokedBy); say why: refunded.
  if (payment === 'refunded') return 'refunded';
  if (payment === 'disputed') return 'disputed';
  if (e.accessRevokedBy || /revoked|cancel/i.test(String(e.status ?? ''))) return 'revoked';
  if (payment === 'comped' || e.source === 'instructor' || e.grantedBy) return 'added';
  if (payment === 'paid') return 'paid';
  return 'free';
}

/** A current student: still has access to the course. */
export function isCurrentEnrollment(e: EnrollmentLike): boolean {
  return ['paid', 'free', 'added'].includes(enrollmentKind(e));
}

export const ENROLLMENT_KIND_LABEL: Record<EnrollmentKind, string> = {
  paid: 'Paid',
  free: 'Free',
  added: 'Added by instructor',
  refunded: 'Refunded',
  disputed: 'Payment disputed',
  revoked: 'Access removed',
};

/** "Paid $15.00", "Free", "Added by instructor", "Refunded"... */
export function enrollmentKindLabel(e: EnrollmentLike, formatMoney: (n: number) => string): string {
  const kind = enrollmentKind(e);
  if (kind === 'paid') {
    const amount = Number(e.amountPaid ?? e.amount);
    return Number.isFinite(amount) && amount > 0 ? `Paid ${formatMoney(amount)}` : 'Paid';
  }
  return ENROLLMENT_KIND_LABEL[kind];
}
